import ReactSelect, { Props as ReactSelectProps, StylesConfig, GroupBase } from 'react-select';

export interface SelectOption {
  label: string;
  value: string | number;
}

interface SimpleSelectProps extends Omit<ReactSelectProps<SelectOption, boolean, GroupBase<SelectOption>>, 'onChange' | 'value'> {
  value?: string | number;
  onChange?: (value: string | number | null) => void;
  values?: Array<string | number>; // selected values when isMulti
  onValuesChange?: (values: Array<string | number>) => void; // change handler when isMulti
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  isSearchable?: boolean;
  compact?: boolean; // smaller control for tight rows such as pagination
}

const customStyles: StylesConfig<SelectOption, boolean> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? 'rgb(var(--color-primary-600))' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgb(var(--color-primary-600) / 0.1)' : 'none',
    '&:hover': {
      borderColor: 'rgb(var(--color-primary-600))',
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'rgb(var(--color-primary-600))'
      : state.isFocused
      ? 'rgb(var(--color-primary-50))'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    padding: '0.5rem 0.75rem',
    '&:active': {
      backgroundColor: 'rgb(var(--color-primary-700))',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 50,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontSize: '0.875rem',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(var(--color-primary-100))',
    borderRadius: '0.25rem',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'rgb(var(--color-primary-800))',
    fontSize: '0.875rem',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'rgb(var(--color-primary-800))',
    '&:hover': {
      backgroundColor: 'rgb(var(--color-primary-200))',
      color: 'rgb(var(--color-primary-900))',
    },
  }),
};

const compactStyles: StylesConfig<SelectOption, boolean> = {
  ...customStyles,
  control: (provided, state) => ({
    ...customStyles.control!(provided, state),
    minHeight: '30px',
  }),
  valueContainer: (provided) => ({ ...provided, padding: '0 0.5rem' }),
  dropdownIndicator: (provided) => ({ ...provided, padding: '2px 4px' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

export default function SimpleSelect({
  value,
  onChange,
  values,
  onValuesChange,
  label,
  error,
  required,
  helperText,
  isSearchable = true,
  options,
  placeholder = 'Select...',
  compact = false,
  ...rest
}: SimpleSelectProps) {
  const isMulti = !!rest.isMulti;
  // Find selected option object(s) from value / values
  const selectedOption = isMulti
    ? (options?.filter((option: any) => values?.includes(option.value)) || []) as SelectOption[]
    : (options?.find((option: any) => option.value === value) || null) as SelectOption | null;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        {...rest}
        value={selectedOption}
        onChange={(option: any) => {
          if (isMulti) {
            onValuesChange?.((option || []).map((o: SelectOption) => o.value));
            return;
          }
          onChange?.(option?.value || null);
        }}
        closeMenuOnSelect={rest.closeMenuOnSelect ?? !isMulti}
        options={options}
        styles={compact ? compactStyles : customStyles}
        classNamePrefix="react-select"
        className="react-select-container"
        isSearchable={isSearchable}
        placeholder={placeholder}
        menuPortalTarget={document.body}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {helperText && !error && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
    </div>
  );
}
